/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type MouseEvent, useCallback } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useAtomValue } from 'jotai';
import { FormattedMessage, type MessageDescriptor } from 'react-intl';
import { useRouter, useRouterActions } from 'react-resource-router';

import { getDocument } from '@atlaskit/browser-apis';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';

import { TWG_MICROSITE_HOME_URL } from '../../../constants';
import { CLI_ROUTE_NAME, CLI_ROUTE_PATH } from '../../../routes';
import { currentDisplayedTierAtom } from '../../state';
import { isModifiedClick } from '../../utils/is-modified-click';

import { messages } from './messages';

/**
 * Smoothly scrolls the section with the matching `id` into view, aligning its
 * top with the viewport top (offset by each section's `scroll-margin-top`,
 * which accounts for the fixed `NavigationBar`).
 *
 * Used by every nav button that maps to an in-page section, so the behaviour
 * stays consistent whether the user clicks "About", "Connectors", "CLI", etc.
 *
 * Reports whether the section was there to scroll to. Not every home view
 * renders every section: the tier3 view only exposes a `connectors` anchor
 * when its metrics request failed, for instance.
 */
const scrollToSection = (sectionId: string): boolean => {
	const section = getDocument()?.getElementById(sectionId);
	if (!section) {
		return false;
	}
	section.scrollIntoView({
		behavior: 'smooth',
		block: 'start',
	});
	return true;
};

/**
 * Rewrites the fragment to the deep-link token so a reload or a shared link
 * resolves the section through `HASH_TO_ANCHOR_ID`, the same way the
 * cross-page `/#<hash>` links do.
 *
 * `replaceState` deliberately leaves no history entry (in-page scrolling is not
 * a navigation) and, unlike assigning `location.hash`, fires no `hashchange`,
 * so the home page's `useScrollToHash` listener does not scroll a second time
 * on top of the smooth scroll above. The current history state is carried over
 * so the router's own entry data survives.
 */
const replaceHash = (hash: string): void => {
	const view = getDocument()?.defaultView;
	if (!view) {
		return;
	}
	const { pathname, search } = view.location;
	view.history.replaceState(view.history.state, '', `${pathname}${search}#${hash}`);
};

const styles = cssMap({
	centerNav: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.100'),
		justifySelf: 'center',
		marginBottom: token('space.negative.050'),
		'@media (max-width: 48rem)': {
			display: 'none',
		},
	},
	button: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		border: 'none',
		borderRadius: token('radius.medium'),
		backgroundColor: 'transparent',
		color: '#B7B9BE',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	buttonActive: {
		color: '#FFFFFF',
		fontWeight: token('font.weight.bold'),
	},
});

type SectionNavItemProps = {
	/** DOM id of the section this item scrolls to on the home page. */
	sectionId: string;
	/**
	 * `HASH_TO_ANCHOR_ID` token for the same section. Used as the fragment this
	 * item writes to the URL, and as the target of the `/#<hash>` link it
	 * becomes on other routes. Not interchangeable with `sectionId`.
	 */
	hash: string;
	message: MessageDescriptor;
};

/**
 * Nav item for a home page section. On the home page it scrolls in-page and
 * syncs the fragment; from `/cli`, where the section does not exist, it becomes
 * an SPA navigation to `/#<hash>` and the home page's `useScrollToHash` scrolls
 * once it mounts.
 */
const SectionNavItem = ({ sectionId, hash, message }: SectionNavItemProps): JSX.Element => {
	const [{ route }] = useRouter();
	const { push } = useRouterActions();
	const homeHref = `${TWG_MICROSITE_HOME_URL}#${hash}`;

	const handleCrossRouteClick = useCallback(
		(event: MouseEvent<HTMLAnchorElement>): void => {
			if (isModifiedClick(event)) {
				return;
			}
			event.preventDefault();
			push(homeHref);
		},
		[push, homeHref],
	);

	// Only sync the fragment once the section has actually been reached, so a
	// view that does not render it cannot leave behind a shared or reloaded
	// URL that resolves to nothing.
	const revealSection = useCallback((): void => {
		if (scrollToSection(sectionId)) {
			replaceHash(hash);
		}
	}, [sectionId, hash]);

	const handleInPageClick = useCallback(
		(event: MouseEvent<HTMLAnchorElement>): void => {
			if (isModifiedClick(event)) {
				return;
			}
			event.preventDefault();
			revealSection();
		},
		[revealSection],
	);

	if (route?.name === CLI_ROUTE_NAME) {
		return (
			// eslint-disable-next-line @atlaskit/design-system/no-html-anchor -- SPA navigation needs native link semantics
			<a href={homeHref} css={styles.button} onClick={handleCrossRouteClick}>
				<FormattedMessage {...message} />
			</a>
		);
	}

	return fg('twg_microsite_accessibility_improvements') ? (
		// eslint-disable-next-line @atlaskit/design-system/no-html-anchor -- in-page hash navigation needs native link semantics
		<a href={`#${hash}`} css={styles.button} onClick={handleInPageClick}>
			<FormattedMessage {...message} />
		</a>
	) : (
		<button type="button" css={styles.button} onClick={revealSection}>
			<FormattedMessage {...message} />
		</button>
	);
};

/**
 * Logged-in nav item for `/cli`. Carries the same "Your Graph" label as the
 * home page section link, but routes back to the home page instead of scrolling,
 * so it applies to every tier.
 */
const HomeNavItem = (): JSX.Element => {
	const { push } = useRouterActions();

	const handleClick = useCallback(
		(event: MouseEvent<HTMLAnchorElement>): void => {
			if (isModifiedClick(event)) {
				return;
			}
			event.preventDefault();
			push(TWG_MICROSITE_HOME_URL);
		},
		[push],
	);

	return (
		// eslint-disable-next-line @atlaskit/design-system/no-html-anchor -- SPA navigation needs native link semantics
		<a href={TWG_MICROSITE_HOME_URL} css={styles.button} onClick={handleClick}>
			<FormattedMessage {...messages.navYourGraph} />
		</a>
	);
};

/**
 * Nav item for the CLI. Routes to the standalone `/cli` page and marks itself
 * as the current page while the visitor is there.
 */
const CLINavItem = (): JSX.Element => {
	const [{ route }] = useRouter();
	const { push } = useRouterActions();
	const isCliPage = route?.name === CLI_ROUTE_NAME;

	const handleClick = useCallback(
		(event: MouseEvent<HTMLAnchorElement>): void => {
			if (isModifiedClick(event)) {
				return;
			}
			event.preventDefault();
			if (!isCliPage) {
				push(CLI_ROUTE_PATH);
			}
		},
		[isCliPage, push],
	);

	return (
		// eslint-disable-next-line @atlaskit/design-system/no-html-anchor -- SPA navigation needs native link semantics
		<a
			href={CLI_ROUTE_PATH}
			css={[styles.button, isCliPage && styles.buttonActive]}
			aria-current={isCliPage ? 'page' : undefined}
			onClick={handleClick}
		>
			<FormattedMessage {...messages.navCli} />
		</a>
	);
};

type NavButtonsProps = {
	ariaLabel: string;
	isLoggedIn: boolean;
};

export const NavButtons = ({ ariaLabel, isLoggedIn }: NavButtonsProps): JSX.Element => {
	const tier = useAtomValue(currentDisplayedTierAtom);
	const [{ route }] = useRouter();

	if (!isLoggedIn) {
		// MCP scrolls to the "Connect your agents" section, which renders the
		// CLI and MCP options side-by-side. There's no tab state to flip on
		// that section, so a plain scroll is enough.
		return (
			<nav css={styles.centerNav} aria-label={ariaLabel}>
				<SectionNavItem sectionId="overview" hash="about" message={messages.navAbout} />
				<SectionNavItem sectionId="connectors" hash="connectors" message={messages.navConnectors} />
				<CLINavItem />
				<SectionNavItem sectionId="getting-started-v2-mcp" hash="mcp" message={messages.navMcp} />
			</nav>
		);
	}

	// `/cli` has no graph sections to link into, so the logged-in nav collapses
	// to a way back to the home page plus the current-page CLI item. That
	// applies to every tier, hence the check sitting ahead of the tier handling
	// below.
	if (route?.name === CLI_ROUTE_NAME) {
		return (
			<nav css={styles.centerNav} aria-label={ariaLabel}>
				<HomeNavItem />
				<CLINavItem />
			</nav>
		);
	}

	if (!tier) {
		return <nav css={styles.centerNav} aria-label={ariaLabel}></nav>;
	}

	return (
		<nav css={styles.centerNav} aria-label={ariaLabel}>
			{tier !== 'tier3' && (
				<SectionNavItem
					sectionId="graph-preview"
					hash="your-graph"
					message={messages.navYourGraph}
				/>
			)}
			<SectionNavItem sectionId="connectors" hash="connectors" message={messages.navConnectors} />
			<CLINavItem />
			<SectionNavItem sectionId="getting-started-v2-mcp" hash="mcp" message={messages.navMcp} />
		</nav>
	);
};
